import { useEffect, useRef, useState } from "react";
import type { OpsApi } from "../useOps";
import { ruleTag } from "@/lib/normalize";

function fmtMs(ms: number | null): string {
  if (ms == null) return "—";
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/** Footer — sources, last-signal high-water, latency, and a scrolling feed. */
export function Footer({ ops }: { ops: OpsApi }) {
  const runRef = useRef<HTMLSpanElement | null>(null);
  const [, force] = useState(0);

  // Tick every second so "Last Signal" ages live — otherwise it only re-rendered
  // on a new signal (ops.version), so the counter looked frozen between signals.
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = runRef.current;
    if (!el) return;
    const signals = ops.getClient()?.signals ?? [];
    const items = signals.slice(0, 7).map((s) => `${ruleTag(s.rule)} · ${s.title}`.toUpperCase());
    const txt = items.join("       ◆       ") || "AWAITING SIGNALS";
    el.textContent = `${txt}       ◆       ${txt}`;
  }, [ops.version, ops]);

  const client = ops.getClient();
  const newest = client?.signals.reduce((m, s) => Math.max(m, s.updatedAt || 0), 0) ?? 0;
  const ageSec = Math.max(0, Math.round((Date.now() - newest) / 1000));
  // Seconds read well while fresh; past 99s a big raw number is noise → switch to minutes.
  const hw = !newest ? "—" : ageSec <= 99 ? `${ageSec}s ago` : `${Math.round(ageSec / 60)}m ago`;

  return (
    <footer id="foot">
      <div className="foot-cell" title="EARLYWIRE Operations Console version">
        <span className="k">Ops Console</span>
        <span className="v">V1.0</span>
      </div>
      <div className="foot-cell" title="Active OSINT data sources feeding the signal engine">
        <span className="k">Sources</span>
        <span className="v">AIS · ADS-B · USGS · GDELT · ENTSO-E</span>
      </div>
      <div className="foot-cell" title="Time since the most recent signal was created or updated">
        <span className="k">Last Signal</span>
        <span className="v">{hw}</span>
      </div>
      <div
        className="foot-cell"
        title={
          ops.lastE2eMs != null
            ? "Engine ingress → this browser, measured on the last signal (server-clock corrected). Hover value in parentheses = median engine-internal latency."
            : "Median internal latency (engine ingress → signal published) across recent signals"
        }
      >
        <span className="k">Latency</span>
        <span className="v">{fmtMs(ops.lastE2eMs ?? ops.medianLatencyMs)}</span>
      </div>
      <div className="foot-cell foot-ticker">
        <span className="run" ref={runRef} />
      </div>
    </footer>
  );
}
